import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { FieldApplicationReport } from "@/api/types";
import "./ReportsFieldApplications.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

const APPLICATION_TYPES = ["chemical", "fertilizer", "seed"] as const;
type ApplicationTypeFilter = "" | (typeof APPLICATION_TYPES)[number];

/** 30 days back from today, UTC — same rolling-window default
 *  `ReportsCropProduction.tsx`'s own `defaultRangeStart` uses, duplicated on purpose
 *  (that file's own reasoning: each report's own default is free to diverge later). */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Field applications log — Reports' one screen for every chemical, fertilizer, and seed
 * application logged against a field-crop, one row per application, for a window the
 * owner chooses. ef-053-4, ADR 0009 §6 ("Reports is one picker" — a report renders
 * inside the template at `/reports/{slug}`, no area tab strip of its own; "All reports"
 * below is the only way back).
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsCropProduction.tsx`'s own remarks give, restated here for the identical
 * reason: an applications log read against a window nobody asked for is a list a
 * farmer did not request. The From/To inputs still carry a sensible rolling-30-day
 * default so the form is never two empty fields.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER, NO FIELD PICKER — same reasoning `ReportsCropProduction.tsx`'s own
 * remarks give for omitting an entity picker: `FieldApplicationsReportEndpoints`
 * resolves the tenant's own single/oldest entity when `entityId` is omitted, which is
 * every farm this booth demo models. The server also accepts an optional `fieldId`
 * narrowing filter (`FieldApplicationsReportEndpoints`' own remarks) — this screen does
 * not surface a field picker for it, the same "one filter control, not two" scope this
 * job's own brief keeps to 2 SP; a future slice can add one without touching the wire
 * contract.
 * </p>
 *
 * <p>
 * COST RENDERS BLANK, NEVER $0.00 — `row.cost` is nullable end to end; a null cost is a
 * true "nothing spent (or not yet known)", not a zero, so it is never coalesced (see
 * `FieldApplicationRowResponse.Cost`'s own remarks on the API side).
 * </p>
 */
export function ReportsFieldApplications() {
  const fromId = useId();
  const toId = useId();
  const typeId = useId();

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);
  const [applicationType, setApplicationType] = useState<ApplicationTypeFilter>("");

  const [report, setReport] = useState<FieldApplicationReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getFieldApplicationsReport({
        from,
        to,
        applicationType: applicationType || undefined,
      });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the field applications log."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsCropProduction.tsx`'s own identical download gives:
   * paying for the whole CSV body on every date change would be wasted work for the
   * visits that never press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getFieldApplicationsCsv({
        from,
        to,
        applicationType: applicationType || undefined,
      });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsCropProduction.tsx`'s own
      // identical download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-field-applications-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the field applications file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="field-applications">
      <PageHeader
        title="Field applications log"
        subtitle="Every chemical, fertilizer and seed application, per field, over a window you choose"
      />

      <a href="/reports" className="field-applications__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="field-applications__panel"
        aria-labelledby="field-applications-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="field-applications-heading" className="field-applications__heading">
          Log
        </h2>

        <form className="field-applications__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="field-applications__field">
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="field-applications__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <div className="field-applications__field">
            <label htmlFor={typeId}>Type</label>
            <select
              id={typeId}
              value={applicationType}
              onChange={(event) => setApplicationType(event.target.value as ApplicationTypeFilter)}
            >
              <option value="">All types</option>
              {APPLICATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type[0]!.toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn--primary field-applications__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="field-applications__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        <Why>
          Rows are sorted by <strong>field</strong>, then <strong>applied date</strong>,
          scoped to From/To exactly.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the field applications log."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="No applications were found."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="field-applications__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="field-applications-heading"
            >
              <table className="field-applications__table ruled-rows">
                <caption className="field-applications__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Crop</th>
                    <th scope="col">Type</th>
                    <th scope="col">Product</th>
                    <th scope="col">Applied</th>
                    <th scope="col" className="field-applications__num">Rate</th>
                    <th scope="col" className="field-applications__num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={`${row.fieldId}-${row.appliedOn}-${index}`}>
                      <th scope="row" className="field-applications__field-name">{row.fieldName}</th>
                      <td>{row.crop}</td>
                      <td>{row.applicationType}</td>
                      <td>{row.product}</td>
                      <td>{row.appliedOn}</td>
                      <td className="field-applications__num">
                        {row.rate != null ? `${row.rate.toFixed(2)}${row.rateUnit ? ` ${row.rateUnit}` : ""}` : ""}
                      </td>
                      <td className="field-applications__num field-applications__cost-amount">
                        {row.cost != null ? formatMoney(row.cost) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="field-applications__export">
              <h3 className="field-applications__export-title">Field applications for your records</h3>
              <p className="field-applications__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary field-applications__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="field-applications__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="field-applications__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
